import type { IResponseData } from "@/shared/types/api";
import { toast } from "vue-sonner";

const messageTypes = {
  Message: "message",
  Alert: "alert",
  Confirm: "confirm",
  Raw: "raw",
} as const;

type MessageType = (typeof messageTypes)[keyof typeof messageTypes];

const businessErrorMessages: Record<string, string> = {
  "base.message.container_cannot_be_deleted_during_use": "使用中的容器不允许删除",
  "base.message.container_cannot_be_deleted_during_use_batch":
    "以下容器正在使用中，不允许删除：{code}",
  "base.message.vehicle_cannot_be_deleted_during_use": "使用中的载具不允许删除",
  "base.message.vehicle_cannot_be_deleted_during_use_batch":
    "以下载具正在使用中，不允许删除：{code}",
  "productAndstock.message.product_lock_stock_reason_null": "库存锁定请填写备注原因",
  "productAndstock.message.product_not_enough_stock_location": "可用库存不足,请刷新后重新进行锁定!",
  "productAndStock.message.product_batch_repeat": "货品批次已经存在",
  "productAndStock.message.product_lock_stock_reason_null": "库存锁定请填写备注原因",
  "productAndStock.message.product_not_enough_stock_location": "可用库存不足,请刷新后重新进行锁定!",
  "stockout.message.exception_outbound_order_not_allowed_to_remove_exception":
    "没有允许解除异常的异常发货单，请检查",
  "stockout.message.not_found_object": "对象不存在",
  "stockout.message.part_of_selected_documents_cannot_be_removed":
    "所选单据中，包含部分不能移出的订单，请检查",
};

export class BusinessRequestError<T = unknown, E = unknown> extends Error {
  readonly code: string;
  readonly description: string;
  readonly extend: E;
  readonly module: string;
  readonly response: IResponseData<T, E>;
  readonly type: MessageType | "";

  constructor(response: IResponseData<T, E>) {
    super(response.description || response.message || "请求失败");
    this.name = "BusinessRequestError";
    this.code = response.code;
    this.description = response.description;
    this.extend = response.extend;
    this.module = response.module;
    this.response = response;
    this.type = getMessageType(response.message);
  }

  get shouldHandleInCaller() {
    return this.type === messageTypes.Confirm;
  }

  notify() {
    if (this.shouldHandleInCaller) {
      return;
    }

    const message = getBusinessErrorMessage(this.response);
    toast.error(message, {
      description: this.description && this.description !== message ? this.description : undefined,
    });
  }
}

export function getMessageType(message?: string): MessageType | "" {
  if (!message) {
    return "";
  }

  const separatorIndex = message.indexOf(".");
  const prefix = separatorIndex === -1 ? message : message.slice(0, separatorIndex);
  return Object.values(messageTypes).includes(prefix as MessageType) ? (prefix as MessageType) : "";
}

function getMessagePath(message?: string) {
  if (!message) {
    return "";
  }

  const separatorIndex = message.indexOf(".");
  if (separatorIndex === -1) {
    return Object.values(messageTypes).includes(message as MessageType) ? "" : message;
  }

  const prefix = message.slice(0, separatorIndex);
  return Object.values(messageTypes).includes(prefix as MessageType)
    ? message.slice(separatorIndex + 1)
    : message;
}

function getExtendValue(extend: unknown, key: string) {
  if (typeof extend !== "object" || extend === null || !(key in extend)) {
    return undefined;
  }

  const value = (extend as Record<string, unknown>)[key];
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return undefined;
}

function formatMessage(template: string, extend: unknown) {
  return template.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    return getExtendValue(extend, key) ?? placeholder;
  });
}

export function getBusinessErrorMessage(response: Partial<IResponseData>) {
  if (response.message === messageTypes.Raw) {
    return response.description || "请求失败";
  }

  const messagePath = getMessagePath(response.message);
  const moduleMessagePath =
    messagePath && response.module ? `${response.module}.message.${messagePath}` : "";
  const configuredMessage = messagePath
    ? businessErrorMessages[messagePath] || businessErrorMessages[moduleMessagePath]
    : undefined;
  if (configuredMessage) {
    return formatMessage(configuredMessage, response.extend);
  }

  return response.description || response.message || "请求失败";
}

export function isBusinessRequestError(error: unknown): error is BusinessRequestError {
  return error instanceof BusinessRequestError;
}

export const isBusinessErrorHandle = isBusinessRequestError;
